(() => {
  const initialized = new WeakSet();

  const initCarousel = (viewport) => {
    if (initialized.has(viewport) || typeof window.EmblaCarousel === 'undefined') return;
    initialized.add(viewport);

    viewport.querySelectorAll('img').forEach((image) => {
      image.setAttribute('draggable', 'false');
    });

    const embla = window.EmblaCarousel(viewport, {
      // Not looped. Reviews are a finite set the reader is meant to get to the
      // end of: an infinite belt hides how many there are and never gives the
      // "I've seen them all" signal, so people keep swiping a track that never
      // resolves. Ending on the last card is the honest affordance here.
      loop: false,
      align: 'start',
      // dragFree = free scrolling: the track follows the finger and coasts
      // instead of snapping card-by-card, which is what removed the "jump"
      // per swipe. trimSnaps still clamps the track to its real bounds so the
      // first card rests on the left inset and the last on the right; the
      // trailing spacer (CSS) is what gives the final card breathing room.
      containScroll: 'trimSnaps',
      dragFree: true,
      skipSnaps: false,
      // Snappier arrow transitions (Embla: higher = slower, range 20–60).
      duration: 20,
    });

    viewport.classList.add('is-embla');

    // Preload the neighbours of the selected slide (wrap-aware for loop) so
    // scrolling never lands on a still-loading lazy image.
    const slides = embla.slideNodes();
    const preloadAround = () => {
      const selected = embla.selectedScrollSnap();
      const count = slides.length;
      [selected, selected + 1, selected - 1, selected + 2].forEach((i) => {
        const slide = slides[((i % count) + count) % count];
        slide?.querySelectorAll('img[loading="lazy"]').forEach((img) => {
          img.loading = 'eager';
        });
      });
    };
    embla.on('select', preloadAround);
    preloadAround();

    const root = viewport.closest('.new-design-reviews') || viewport.parentElement;
    const prev = root?.querySelector('[data-embla-prev]');
    const next = root?.querySelector('[data-embla-next]');

    prev?.addEventListener('click', () => embla.scrollPrev());
    next?.addEventListener('click', () => embla.scrollNext());

    // Hide the prev arrow at the start and the next arrow at the end — same
    // edge behaviour as the product gallery. canScrollPrev/Next track the
    // scroll position (works with dragFree, not just snap-to-slide), so the
    // arrows update continuously as the track is dragged. Toggled via
    // .is-hidden so the arrow keeps its slot (visibility, not display).
    const syncArrows = () => {
      prev?.classList.toggle('is-hidden', !embla.canScrollPrev());
      next?.classList.toggle('is-hidden', !embla.canScrollNext());
    };
    embla.on('select', syncArrows);
    embla.on('scroll', syncArrows);
    embla.on('reInit', syncArrows);
    syncArrows();

    // Closed-hand cursor while dragging. Embla v8 doesn't toggle a class on the
    // viewport itself, so drive it from its pointerDown/pointerUp events; the
    // CSS grab/grabbing rules key off .is-dragging (desktop pointers only).
    embla.on('pointerDown', () => viewport.classList.add('is-dragging'));
    embla.on('pointerUp', () => viewport.classList.remove('is-dragging'));

    // Re-measure once late images have loaded.
    window.addEventListener('load', () => embla.reInit(), { once: true });
  };

  const initAll = (root = document) => {
    if (typeof window.EmblaCarousel === 'undefined') return false;
    root.querySelectorAll('[data-reviews-carousel]').forEach(initCarousel);
    return true;
  };

  const initWhenReady = (root = document, attemptsLeft = 60) => {
    if (initAll(root) || attemptsLeft <= 0) return;
    window.setTimeout(() => initWhenReady(root, attemptsLeft - 1), 50);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initWhenReady(), { once: true });
  } else {
    initWhenReady();
  }

  window.addEventListener('load', () => initWhenReady(), { once: true });
  document.addEventListener('shopify:section:load', (event) => initWhenReady(event.target));
})();
